/**
 * The one error shape, as the web version sees it (a copy of the phone's
 * apps/mobile/src/api/errors.ts, without the phone-only "too old" case).
 *
 * Every failure from the API arrives as `{ error: { code, message, details? },
 * requestId }`. The code is stable and the message is English; the web branches
 * on the code and shows its own translation (errorText in i18n/provider.tsx).
 * Nothing here formats anything for a person.
 */

/** Field-keyed validation messages: `{ password: "At least 8 characters" }`. */
export type FieldErrors = Record<string, string>;

/** The codes that mean the session is over and refreshing cannot save it. */
const SESSION_OVER = new Set([
  "unauthenticated",
  "invalid_access_token",
  "invalid_refresh_token",
  "refresh_token_reused",
  // Deleted by an owner: everybody in the pharmacy is signed out.
  "pharmacy_deleted",
]);

export class ApiError extends Error {
  constructor(
    /** The stable code. `network` and `timeout` are ours; the rest are the server's. */
    readonly code: string,
    /** The server's English message, kept only as a fallback for unknown codes. */
    readonly serverMessage: string,
    readonly status: number,
    readonly details?: FieldErrors,
    /** Ties a report from a person to a line in the server's logs. */
    readonly requestId?: string | null,
    /** How long the server asked us to wait before trying again, when it said. */
    readonly retryAfterSeconds?: number,
  ) {
    super(`${code}: ${serverMessage}`);
    this.name = "ApiError";
  }

  /** The server is busy (429), not refusing: worth asking again later, unchanged. */
  get isBusy(): boolean {
    return this.status === 429;
  }

  /** The session is gone and cannot be recovered by refreshing. */
  get isAuthFailure(): boolean {
    return this.status === 401 && SESSION_OVER.has(this.code);
  }

  /**
   * The request never reached the server, so nothing happened and it is safe to
   * retry. A 500 is deliberately NOT included: the server may have saved before
   * failing, and a blind retry could record something twice.
   */
  get isOffline(): boolean {
    return this.code === "network" || this.code === "timeout";
  }

  /** The plan has ended and the pharmacy is read-only (Phase 14). */
  get isPlanExpired(): boolean {
    return this.code === "plan_expired";
  }

  /**
   * The number some translated messages need (minutes left on a lock, a minimum
   * length), read out of the server's English message. Fragile, and right only
   * because it serves two messages; a third would deserve a real field.
   */
  get amount(): number | undefined {
    const match = /\b(\d+)\b/.exec(this.serverMessage);
    return match ? Number(match[1]) : undefined;
  }
}

/** Build an ApiError from a response the server has already shaped. */
export async function apiErrorFrom(res: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    // A proxy, a captive portal, or a crash before the server's filter ran.
    return new ApiError("unknown", `HTTP ${res.status}`, res.status);
  }

  const shaped = body as {
    error?: { code?: string; message?: string; details?: FieldErrors };
    requestId?: string | null;
  };

  return new ApiError(
    shaped.error?.code ?? "unknown",
    shaped.error?.message ?? `HTTP ${res.status}`,
    res.status,
    shaped.error?.details,
    shaped.requestId ?? null,
    retryAfter(res.headers.get("Retry-After")),
  );
}

/** `Retry-After` in seconds. Only the seconds form: the server never sends a date. */
export function retryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header.trim());
  return Number.isFinite(seconds) && seconds >= 0 ? Math.ceil(seconds) : undefined;
}
